import React, { useCallback, useContext } from 'react';
import { styled } from '@compiled/react';
import { xcss, Anchor } from '@atlaskit/primitives';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import TeamAvatar from '@atlaskit/teams-avatar';
import {
	JiraPreviewPanelsContext,
	useGlobalObjectPreviewPanels,
} from '@atlassian/jira-preview-panels/src/PreviewPanelsProvider.tsx';
import { useIsModal } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getIdFromAri } from '@atlassian/jira-compass-common/src/common/utils.tsx';
import TeamProfileCard from '@atlassian/jira-profilecard-next/src/ui/team-profilecard/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { TeamReadViewWithGppProps } from './types.tsx';
import messages from './messages.tsx';

/**
 * The TeamReadViewWithGPP will show a read only view of Team field, which can open up GPP
 * @param props [TeamReadViewProps](./types.tsx)
 */
export const TeamReadViewWithGpp = ({
	teamName,
	teamId,
	ariId,
	maxLines,
	triggerLinkType = 'link',
	showTeamPreview = false,
}: TeamReadViewWithGppProps): React.JSX.Element => {
	const isPreviewPanelContextAvailable = useContext(JiraPreviewPanelsContext);
	let isPreviewPanelAvailable = !!isPreviewPanelContextAvailable;
	const { formatMessage } = useIntl();

	const resolvedTeamId = teamId || (ariId ? getIdFromAri(ariId) : '');
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const teamUrl = `${window.location.origin}/jira/people/team/${resolvedTeamId}`;

	let openPreviewPanelWithTeamData = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		(event: React.MouseEvent | React.KeyboardEvent) => {
			/* empty */
		},
		[],
	);

	if (isPreviewPanelAvailable) {
		const { openPreviewPanel, isPreviewPanelAvailable: isPreviewPanelAvailableInContext } =
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useGlobalObjectPreviewPanels();
		isPreviewPanelAvailable =
			isPreviewPanelAvailable && isPreviewPanelAvailableInContext({ ari: ariId || '' });

		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		openPreviewPanelWithTeamData = useCallback(
			(event: React.MouseEvent | React.KeyboardEvent) => {
				// For keyboard events, only respond to Enter and Space
				if ('key' in event && event.key !== 'Enter' && event.key !== ' ') {
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				openPreviewPanel({
					ari: ariId,
					url: teamUrl,
					name: 'identity-team',
				});
			},
			[ariId, openPreviewPanel, teamUrl],
		);
	}

	const handleLinkClick = useCallback((event: React.MouseEvent | React.KeyboardEvent) => {
		event.stopPropagation();
	}, []);

	const ariaLabel = formatMessage(messages.teamPreviewLabel, { teamName });

	const isModal = useIsModal();
	const canShowPreview = !isModal && isPreviewPanelAvailable;

	const teamNameContent = canShowPreview ? (
		<TeamNameWrapper
			onClick={openPreviewPanelWithTeamData}
			onKeyDown={openPreviewPanelWithTeamData}
			role="button"
			tabIndex={0}
			aria-label={ariaLabel}
		>
			<Text maxLines={maxLines}>{teamName}</Text>
		</TeamNameWrapper>
	) : (
		<Anchor href={teamUrl} target="_blank" onClick={handleLinkClick} xcss={teamLinkStyles}>
			<Text maxLines={maxLines}>{teamName}</Text>
		</Anchor>
	);

	const gppContent = fg('avatar_for_jira_team_field') ? (
		<Flex gap="space.050" alignItems="center">
			<TeamAvatar size="small" teamId={teamId} />
			{teamNameContent}
		</Flex>
	) : (
		teamNameContent
	);

	if (showTeamPreview) {
		return (
			<TeamProfileCard
				teamId={ariId}
				trigger="hover"
				position="left-start"
				triggerLinkType={triggerLinkType}
			>
				{gppContent}
			</TeamProfileCard>
		);
	}

	return gppContent;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TeamNameWrapper = styled.span({
	cursor: 'pointer',
	textDecoration: 'none',
	'&:hover': {
		textDecoration: 'underline',
	},
});

const teamLinkStyles = xcss({
	cursor: 'pointer',
	textDecoration: 'none',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	color: 'color.text',
	':hover': {
		textDecoration: 'underline',
		color: 'color.text',
	},
	':visited': {
		color: 'color.text',
	},
	':focus': {
		color: 'color.text',
	},
});
